import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { refreshHabitLifeDomainSuggestionsSoon } from './lifeBalance.api.js';
export const habitSchema = z.object({
    id: z.string(),
    name: z.string(),
    parentId: z.string().optional().nullable(),
    rrule: z.string().optional().nullable(),
    startDate: z.string().optional().nullable(),
    sortKey: z.number().optional().nullable(),
    targetCount: z.number().optional().nullable(),
    isRoutine: z.boolean().optional().nullable(),
    lifeDomainId: z.string().optional().nullable(),
    // The goal (or milestone) and plan item the habit was created from
    goal: z
        .object({
        goalRefinementId: z.string(),
        milestoneId: z.string().optional().nullable(),
        planItemId: z.string(),
    })
        .optional()
        .nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const habitCompletionSchema = z.object({
    id: z.string(),
    habitId: z.string(),
    completionDate: z.string(),
    count: z.number().optional().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createHabitResponseSchema = z.object({
    habitId: z.string(),
});
export const habitsResponseSchema = z.object({
    habits: z.array(habitSchema),
});
export const completionsResponseSchema = z.object({
    completions: z.array(habitCompletionSchema),
});
export const recordCompletionResponseSchema = z.object({
    completionId: z.string(),
});
// Form schema shared by the create and edit habit/routine screens in web.
// A habit inside a routine is performed on its routine's schedule, so only a top-level
// habit or routine carries a recurrence and a start date of its own.
export const habitFormSchema = z
    .object({
    name: z.string().min(1, 'Name is required').max(200, 'Name must be 200 characters or fewer'),
    parentId: z.string().optional(),
    isRoutine: z.boolean().optional(),
    rrule: z.string().optional(),
    startDate: z.string().optional(),
    targetCount: z.number().int().positive().optional(),
    lifeDomainId: z.string().optional().nullable(),
    // Only set when creating: a habit's goal or milestone is fixed once it exists
    goal: z.object({ goalRefinementId: z.string(), milestoneId: z.string().optional() }).optional(),
})
    .refine((data) => {
    // A top-level habit or routine must have a recurrence and a start date.
    if (!data.parentId && (!data.rrule || !data.startDate)) {
        return false;
    }
    return true;
}, {
    message: 'Choose how often this repeats',
    path: ['rrule'],
});
export const useCreateHabit = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => {
            return authenticatedRequest({
                url: '/habits',
                method: 'POST',
                data,
            }, createHabitResponseSchema);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['habits'] });
            // A habit created for a goal or milestone is a new plan item of it
            queryClient.invalidateQueries({ queryKey: ['goalRefinement'] });
            queryClient.invalidateQueries({ queryKey: ['goalRefinements'] });
            refreshHabitLifeDomainSuggestionsSoon(queryClient);
        },
    });
};
export const useGetUserHabits = (authenticatedRequest, autoFetch = true) => {
    return useQuery({
        queryKey: ['habits'],
        queryFn: () => authenticatedRequest({ url: '/habits', method: 'GET' }, habitsResponseSchema),
        enabled: autoFetch,
    });
};
export const useGetCompletions = (authenticatedRequest, params, autoFetch = true) => {
    return useQuery({
        queryKey: ['habits', 'completions', params],
        queryFn: () => authenticatedRequest({ url: '/habits/completions', method: 'GET', params }, completionsResponseSchema),
        enabled: autoFetch,
    });
};
export const useDeleteCompletion = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => {
            return authenticatedRequest({
                url: `/habits/completions/${id}`,
                method: 'DELETE',
            }, z.any());
        },
        // Returned, not fired and forgotten, so a caller's own onSuccess runs once the
        // refetch has landed -- an optimistic toggle can then drop its pending state
        // without the row flickering back to its old value in between.
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['habits'] }),
    });
};
export const useGetHabit = (authenticatedRequest, id, autoFetch = true) => {
    return useQuery({
        queryKey: ['habits', id],
        queryFn: () => authenticatedRequest({ url: `/habits/${id}`, method: 'GET' }, habitSchema),
        enabled: autoFetch && !!id,
    });
};
export const useUpdateHabit = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => {
            return authenticatedRequest({
                url: `/habits/${id}`,
                method: 'PUT',
                data,
            }, z.any());
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['habits'] });
            refreshHabitLifeDomainSuggestionsSoon(queryClient);
        },
    });
};
export const useDeleteHabit = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => {
            return authenticatedRequest({
                url: `/habits/${id}`,
                method: 'DELETE',
            }, z.any());
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['habits'] });
        },
    });
};
/**
 * A single routine's habits. Note that `useGetUserHabits` already returns the whole
 * tree, children included, so a screen showing several routines should group that one
 * response rather than calling this per routine.
 */
export const useGetRoutineHabits = (authenticatedRequest, id, autoFetch = true) => {
    return useQuery({
        queryKey: ['habits', 'routine', id],
        queryFn: () => authenticatedRequest({ url: `/habits/${id}/habits`, method: 'GET' }, habitsResponseSchema),
        enabled: autoFetch && !!id,
    });
};
export const useRecordCompletion = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => {
            return authenticatedRequest({
                url: `/habits/${id}/completions`,
                method: 'POST',
                data,
            }, recordCompletionResponseSchema);
        },
        // See useDeleteCompletion: awaited by returning it, so an optimistic toggle can
        // clear its pending state against already-refetched completions.
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['habits'] }),
    });
};
